import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, useFieldArray } from "react-hook-form";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Loader from "../../components/Loaders/Loader";
import ProduceRowsTable from "../../features/creativeTemplates/produce/ProduceRowsTable";
import ProduceTemplateCard from "../../features/creativeTemplates/produce/ProduceTemplateCard";
import TemplateLayoutPreview from "../../features/creativeTemplates/editor/TemplateLayoutPreview";
import ProduceResultsGallery from "../../features/creativeTemplates/produce/ProduceResultsGallery";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { getIsBlockedActions } from "../../actions/getters";
import { getKeyDatabase } from "../../services/public";
import {
  pruneCreativeTemplatePendingDeletes,
  startListenerCreativeTemplate,
  startListenerCreativeTemplateCards,
  startProduceCreativeTemplate,
  startRetryFailedProduceCreativeTemplate,
  stopListenerCreativeTemplate,
  stopListenerCreativeTemplateCards,
} from "../../actions/creativeTemplates";
import {
  startListLastCreativeTemplateProduces,
  startListenerCreativeTemplateProduce,
  startRehostProduceRowImages,
  stopListenerCreativeTemplateProduce,
  startDeleteProduceCreatives,
} from "../../actions/creativeTemplatesProduce";
import { CREATIVE_TEMPLATE_PRODUCE_STATUS } from "../../utils/constants";
import { CONTENT_LANGUAGE_PRESET } from "../../utils/catalogs/contentLanguage";
import { useEnsureContentLanguageField } from "../../hooks/useAdvertiserContentLanguage";
import {
  LOADING_INFORMATION,
  TEMPLATES_PRODUCE_CHANGE_TEMPLATE_DESCRIPTION,
  TEMPLATES_PRODUCE_CHANGE_TEMPLATE_TITLE,
  TEMPLATES_PRODUCE_CONFIG_FILENAME,
  TEMPLATES_PRODUCE_CSV_APPEND,
  TEMPLATES_PRODUCE_CSV_DESCRIPTION,
  TEMPLATES_PRODUCE_CSV_EMPTY,
  TEMPLATES_PRODUCE_CSV_ERROR,
  TEMPLATES_PRODUCE_CSV_EXAMPLE_FILENAME,
  TEMPLATES_PRODUCE_CSV_EXAMPLE_TEXT,
  TEMPLATES_PRODUCE_CSV_REPLACE,
  TEMPLATES_PRODUCE_CSV_TITLE,
  TEMPLATES_PRODUCE_DESCRIPTION,
  TEMPLATES_PRODUCE_SELECT_TEMPLATE,
  TEMPLATES_PRODUCE_TITLE,
  TEMPLATES_PRODUCE_VALIDATION_ERROR,
  TEMPLATES_PRODUCE_VALIDATION_HIGHLIGHT,
  TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS,
  TEMPLATES_PRODUCE_VALIDATION_NO_ROWS,
  TEMPLATES_PRODUCE_VALIDATION_TOO_MANY_ROWS,
  TEMPLATES_PRODUCE_VALIDATION_VARIATIONS,
  TEMPLATES_PRODUCE_IMAGES_UNUSABLE,
  TEMPLATES_PRODUCE_ACTIVE_RUN,
  BUTTON_CLOSE,
  BUTTON_GO_BACK,
  TEMPLATES_UNTITLED,
} from "../../i18n/keysTranslations";
import {
  clampProduceVariationCount,
  collectProduceImageUrls,
  createEmptyRow,
  getRenamedColumnKeys,
  mapStoredRowsToColumns,
  createVariationRows,
  getFailedProduceImageRowIndexes,
  getInvalidProduceCellKeysByRow,
  hasInvalidHighlightPhrase,
  getProduceProductionRowCount,
  getProducePendingCount,
  getProduceSummary,
  getLatestProduceRun,
  listProduceFailedSlots,
  rowsHaveFailedProduceImages,
  hasActiveProduceRun,
  hasNonEmptyRows,
  isValidProduceVariationCount,
  PRODUCE_MAX_ROWS,
  listProduceColumns,
  listProduceCreatives,
  normalizeStoredIDs,
  normalizeStoredRows,
  parseProduceVariationCount,
  resolveSelectedArtboardIDs,
  scrollToProduceResults,
  scrollToProduceRow,
  toggleSelectedID,
} from "../../features/creativeTemplates/produce/produceRows";
import {
  downloadProduceFormatCsv,
  downloadProduceRowsCsv,
  parseProduceCsvFile,
} from "../../features/creativeTemplates/produce/produceCsv";
import { canProduceCreativeTemplate } from "../../features/creativeTemplates/utils/layerTree";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import { useTemplatesModals } from "../../contexts/templatesModalsContext";
import {
  getArtboardChipLabel,
  getArtboardLabel,
  listArtboardIDs,
} from "../../features/creativeTemplates/utils/artboards";
import { stripToSlideArtboards } from "../../features/creativeTemplates/utils/continuousStripArtboards";
import {
  getFormatArtboards,
  getProduceSlideCount,
  isContinuousLayout,
  listAllCells,
} from "../../features/creativeTemplates/utils/slides";
import {
  getProduceSlidesPerGalleryItem,
  groupProduceCreativesAsGalleryItems,
  groupProduceFailedSlotsAsGalleryItems,
  resolveProduceGallerySlideItem,
} from "../../features/creativeTemplates/produce/produceGalleryItems";
import ModalCreativeTemplateEdit from "../../components/Modal/ModalCreativeTemplateEdit";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import useTemplatesAddon from "../../hooks/useTemplatesAddon";
import FeatureAddonPaywallDialog from "../../components/FeatureAddons/FeatureAddonPaywallDialog";
import { FEATURE_ADDON_KEYS } from "../../actions/featureAddons";

const PRODUCE_HISTORY_LIMIT = 100;
const PRODUCE_HISTORY_DEBOUNCE_MS = 400;

const TemplateProduce = ({
  initialTemplateID = "",
  initialProduceID = "",
  initialLanguage = "",
  initialArtboardIDs = [],
  onBackToList = () => {},
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const pendingDeleteIDs = useSelectorApp(
    (state) => state.creativeTemplates?.pendingDeleteIDs || {}
  );
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const {
    open: templatesPaywallOpen,
    status: templatesPaywallStatus,
    isLoading: templatesPaywallLoading,
    isCheckingOut: templatesCheckingOut,
    closePaywall: closeTemplatesPaywall,
    checkoutTier: checkoutTemplatesTier,
    blockIfProduceBatchExceeded,
  } = useTemplatesAddon();
  const { openTemplateEditor } = useTemplatesModals();
  const { role } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });
  const cardsListenerRef = useRef(null);
  const docListenerRef = useRef(null);
  const produceListenerRef = useRef(null);
  const hydratedProduceIDRef = useRef(null);
  const ensuredProduceIDRef = useRef(false);
  const rowRefs = useRef({});
  const resultsRef = useRef(null);
  const pendingScrollToResultsRef = useRef(Boolean(initialProduceID));
  const wasProducingRef = useRef(false);
  const [cards, setCards] = useState({});
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const [doc, setDoc] = useState(null);
  const [invalidRowIndexes, setInvalidRowIndexes] = useState([]);
  const [invalidCellKeysByRow, setInvalidCellKeysByRow] = useState({});

  const clearInvalidProduceMarks = () => {
    setInvalidRowIndexes([]);
    setInvalidCellKeysByRow({});
  };

  const applyInvalidProduceMarks = ({ nextRows, nextColumns }) => {
    const byRow = getInvalidProduceCellKeysByRow({
      rows: nextRows,
      columns: nextColumns,
    });
    const nextIndexes = Object.keys(byRow).map(Number);
    setInvalidCellKeysByRow(byRow);
    setInvalidRowIndexes(nextIndexes);
    return nextIndexes;
  };

  const clearInvalidProduceCell = ({ index, key }) => {
    setInvalidCellKeysByRow((current) => {
      const keys = current[index];
      if (!keys?.includes(key)) return current;
      const remaining = keys.filter((item) => item !== key);
      const next = { ...current };
      if (remaining.length) next[index] = remaining;
      else delete next[index];
      setInvalidRowIndexes(Object.keys(next).map(Number));
      return next;
    });
  };
  const [isStartingProduce, setIsStartingProduce] = useState(false);
  const [isRetryingProduce, setIsRetryingProduce] = useState(false);
  const [produceRecord, setProduceRecord] = useState(null);
  const [isPickingTemplate, setIsPickingTemplate] = useState(false);
  const [selectedArtboardIDs, setSelectedArtboardIDs] = useState([]);
  const [artboardHydrateNonce, setArtboardHydrateNonce] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [produceID, setProduceID] = useState(initialProduceID || "");
  const [pendingTargetCount, setPendingTargetCount] = useState(0);
  const [checkingImageUrls, setCheckingImageUrls] = useState(() => new Set());
  const [failedImageUrls, setFailedImageUrls] = useState(() => new Set());
  const rehostGenerationRef = useRef(0);
  const rehostGenerationByUrlRef = useRef({});
  const initialStoredArtboardIDs = normalizeStoredIDs({
    value: initialArtboardIDs,
  });
  const storedArtboardIDsRef = useRef(initialStoredArtboardIDs);
  const lockHydratedArtboardsRef = useRef(initialStoredArtboardIDs.length > 0);
  const lockHydratedLanguageRef = useRef(Boolean(initialLanguage));

  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      templateID: initialTemplateID || "",
      language: "",
      variationCount: 1,
      rows: [],
    },
  });

  useEnsureContentLanguageField({
    preset: CONTENT_LANGUAGE_PRESET.TEMPLATES,
    setValue,
    getValues,
    contextLanguage: initialLanguage || null,
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "rows",
  });

  const templateID = watch("templateID");
  const language = watch("language");
  const variationCount = watch("variationCount");
  const rows = watch("rows");

  const produceableCards = useMemo(
    () =>
      Object.values(cards)
        .filter(
          (card) =>
            card?.id &&
            !pendingDeleteIDs[card.id] &&
            canProduceCreativeTemplate({
              nodeCount: card.nodeCount,
              name: card.name,
            })
        )
        .sort(
          (left, right) =>
            (right.updateTime || right.updatedAt || 0) -
            (left.updateTime || left.updatedAt || 0)
        ),
    [cards, pendingDeleteIDs]
  );

  const selectedCard = produceableCards.find((card) => card.id === templateID);
  const columns = useMemo(
    () =>
      listProduceColumns({
        variables: doc?.variables || {},
        nodes: doc?.nodes || {},
        artboards: doc?.artboards || {},
        slides: doc?.slides || [],
        layoutKind: doc?.layoutKind,
        assets: doc?.assets || {},
      }),
    [doc]
  );
  const produceColumnKeys = useMemo(
    () => columns.map((column) => column.key).join("\u0001"),
    [columns]
  );
  const produceStatus = produceRecord?.status || "";
  const isProducing =
    isStartingProduce ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING ||
    hasActiveProduceRun({ produceRecord });
  const canEditRows = Boolean(
    templateID && columns.length > 0 && !isLoadingDoc && !isProducing
  );
  const isCheckingImages = checkingImageUrls.size > 0;
  const hasUnusableImages = rowsHaveFailedProduceImages({
    rows,
    columns,
    failedUrls: failedImageUrls,
  });
  const imageInvalidRowIndexes = getFailedProduceImageRowIndexes({
    rows,
    columns,
    failedUrls: failedImageUrls,
  });
  const tableInvalidRowIndexes = [
    ...new Set([...invalidRowIndexes, ...imageInvalidRowIndexes]),
  ];
  const produceSlideCount = getProduceSlideCount({
    layoutKind: doc?.layoutKind,
    slides: doc?.slides || [],
    layoutConfigurations: doc?.layoutConfigurations,
  });
  const slidesPerGalleryItem = getProduceSlidesPerGalleryItem({
    slideCount: produceSlideCount,
  });
  const galleryItems = useMemo(
    () =>
      groupProduceCreativesAsGalleryItems({
        items: listProduceCreatives({ creatives: produceRecord?.creatives }),
        slidesPerItem: slidesPerGalleryItem,
        hideIncompleteGroups: isProducing,
      }),
    [isProducing, produceRecord, slidesPerGalleryItem]
  );
  const produceSummary = useMemo(
    () => getProduceSummary({ produceRecord }),
    [produceRecord]
  );
  const failedGalleryItems = useMemo(
    () =>
      groupProduceFailedSlotsAsGalleryItems({
        slots: listProduceFailedSlots({ produceRecord }),
        succeededItems: galleryItems,
        slidesPerItem: slidesPerGalleryItem,
      }),
    [galleryItems, produceRecord, slidesPerGalleryItem]
  );
  const latestProduceRun = useMemo(
    () => getLatestProduceRun({ produceRecord }),
    [produceRecord]
  );
  const formatArtboards = useMemo(() => getFormatArtboards({ doc }), [doc]);
  const availableArtboardIDs = useMemo(() => {
    const fromDoc = listArtboardIDs({ artboards: formatArtboards });
    if (fromDoc.length > 0) return fromDoc;
    if (selectedCard?.artboardIDs?.length) return selectedCard.artboardIDs;
    return [];
    // eslint-disable-next-line
  }, [doc, formatArtboards, selectedCard]);
  const availableArtboardIDsKey = availableArtboardIDs.join(",");
  const selectedArtboardCount = Math.max(selectedArtboardIDs.length, 0);
  const produceCells = useMemo(
    () =>
      listAllCells({
        layoutKind: doc?.layoutKind,
        slides: doc?.slides || [],
        artboards: formatArtboards,
        artboardIDs:
          selectedArtboardIDs.length > 0
            ? selectedArtboardIDs
            : availableArtboardIDs,
      }),
    [
      availableArtboardIDs,
      formatArtboards,
      doc?.layoutKind,
      doc?.slides,
      selectedArtboardIDs,
    ]
  );
  // Continuous docs list one cell per format; each yields N slide creatives.
  const continuousCellMultiplier = isContinuousLayout({
    layoutKind: doc?.layoutKind,
    slides: doc?.slides || [],
  })
    ? produceSlideCount
    : 1;
  const produceCellCount = Math.max(
    produceCells.length * continuousCellMultiplier,
    selectedArtboardCount,
    1
  );
  const hasVariables = columns.length > 0;
  const showVariationCount = Boolean(templateID) && !isLoadingDoc;
  const variationRowCount = parseProduceVariationCount({
    value: variationCount,
  });
  const rowCount = hasVariables ? fields.length : variationRowCount;
  const productionRowCount = getProduceProductionRowCount({
    rowCount,
    variationCount,
    hasVariables,
  });
  const totalCount = productionRowCount * produceCellCount;
  const expectedCreativeCount = Math.max(
    Number(produceRecord?.targetCreativeCount) || 0,
    pendingTargetCount
  );
  const pendingCount = getProducePendingCount({
    produceRecord,
    isStartingProduce,
    expectedCreativeCount,
    slidesPerItem: slidesPerGalleryItem,
  });
  const showGallery =
    isProducing ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.SUCCESS ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.PARTIAL ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.ERROR ||
    galleryItems.length > 0 ||
    failedGalleryItems.length > 0;

  const templateOptions = produceableCards.map((card) => ({
    value: card.id,
    label: card.name?.trim() || t(TEMPLATES_UNTITLED),
    previewSrc: card.thumbnailUrl || "",
    previewNode: <TemplateLayoutPreview card={card} />,
  }));
  const labelArtboards = isContinuousLayout({
    layoutKind: doc?.layoutKind,
    slides: doc?.slides || [],
  })
    ? stripToSlideArtboards({
        artboards: formatArtboards,
        slideCount: produceSlideCount,
      })
    : formatArtboards;
  const formatOptions = availableArtboardIDs.map((artboardID) => ({
    value: artboardID,
    label: labelArtboards?.[artboardID]
      ? getArtboardLabel({
          artboard: labelArtboards[artboardID],
          artboardID,
        })
      : getArtboardChipLabel({
          artboardID,
          cardArtboard: selectedCard?.artboard,
        }),
  }));
  const showFormatToggles = availableArtboardIDs.length > 1;
  const showTemplateSelect =
    produceableCards.length > 0 && (!templateID || isPickingTemplate);
  const templateName = selectedCard?.name || doc?.name || "";

  useEffect(() => {
    if (produceID) {
      ensuredProduceIDRef.current = true;
      return;
    }
    if (ensuredProduceIDRef.current) return;
    ensuredProduceIDRef.current = true;
    setProduceID(dispatch(getKeyDatabase()));
  }, [dispatch, produceID]);

  useLayoutEffect(() => {
    if (isProducing) {
      wasProducingRef.current = true;
    } else if (wasProducingRef.current) {
      wasProducingRef.current = false;
      pendingScrollToResultsRef.current = true;
    }

    if (!pendingScrollToResultsRef.current) return;
    if (!showGallery || !resultsRef.current) return;
    pendingScrollToResultsRef.current = false;
    scrollToProduceResults({ resultsRef });
  }, [showGallery, isStartingProduce, isProducing, galleryItems.length]);

  useEffect(() => {
    dispatch(
      startListenerCreativeTemplateCards({
        onUpdatedDataCallback: (value) => {
          const nextCards = value || {};
          setCards(nextCards);
          setIsLoadingCards(false);
          dispatch(
            pruneCreativeTemplatePendingDeletes({
              existingIDs: Object.keys(nextCards),
            })
          );
        },
        onReferenceAvailable: (unsubscribe) => {
          cardsListenerRef.current = unsubscribe;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateCards({
          unsubscribe: cardsListenerRef.current,
        })
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoadingCards) return;
    const currentID = getValues("templateID");
    if (currentID && !produceableCards.some((card) => card.id === currentID)) {
      setValue("templateID", "");
      replace([]);
      setDoc(null);
      clearInvalidProduceMarks();
    }
  }, [
    getValues,
    isLoadingCards,
    produceableCards,
    produceID,
    replace,
    setValue,
  ]);

  useEffect(() => {
    if (!templateID) {
      setDoc(null);
      setIsLoadingDoc(false);
      return undefined;
    }

    setIsLoadingDoc(true);
    dispatch(
      startListenerCreativeTemplate({
        templateID,
        onUpdatedDataCallback: (value) => {
          setDoc(value?.doc || null);
          setIsLoadingDoc(false);
        },
        onReferenceAvailable: (dbRef) => {
          docListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplate({ listenerRef: docListenerRef.current })
      );
    };
  }, [dispatch, templateID]);

  useEffect(() => {
    hydratedProduceIDRef.current = null;
    setProduceRecord(null);
    setPendingTargetCount(0);
    if (!produceID) return undefined;

    dispatch(
      startListenerCreativeTemplateProduce({
        produceID,
        onUpdatedDataCallback: (value) => {
          setProduceRecord(value);
          if (!value || hydratedProduceIDRef.current === produceID) return;
          hydratedProduceIDRef.current = produceID;
          if (value.templateID) setValue("templateID", value.templateID);
          if (value.language && !lockHydratedLanguageRef.current) {
            setValue("language", value.language);
          }
          if (!lockHydratedArtboardsRef.current) {
            storedArtboardIDsRef.current = normalizeStoredIDs({
              value: value.artboardIDs,
            });
          } else {
            storedArtboardIDsRef.current = normalizeStoredIDs({
              value: storedArtboardIDsRef.current,
            });
          }
          setArtboardHydrateNonce((nonce) => nonce + 1);
          const storedRows = normalizeStoredRows({ rows: value.rows });
          if (storedRows.length > 0) replace(storedRows);
          if (value.variationCount != null) {
            setValue(
              "variationCount",
              clampProduceVariationCount({ value: value.variationCount })
            );
          }
        },
        onReferenceAvailable: (dbRef) => {
          produceListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateProduce({
          listenerRef: produceListenerRef.current,
        })
      );
    };
    //eslint-disable-next-line
  }, [dispatch, produceID]);

  useEffect(() => {
    if (!availableArtboardIDsKey) {
      setSelectedArtboardIDs([]);
      return;
    }
    const availableIDs = availableArtboardIDsKey.split(",");
    const storedIDs = storedArtboardIDsRef.current;
    setSelectedArtboardIDs((prev) =>
      resolveSelectedArtboardIDs({
        availableIDs,
        selectedIDs: prev,
        storedIDs,
      })
    );
    if (
      storedIDs.length > 0 &&
      storedIDs.every((id) => availableIDs.includes(id))
    ) {
      storedArtboardIDsRef.current = [];
    }
  }, [templateID, availableArtboardIDsKey, artboardHydrateNonce]);

  const [lastProduceRecords, setLastProduceRecords] = useState([]);
  useEffect(() => {
    setLastProduceRecords([]);
    if (!templateID) return undefined;
    let cancelled = false;
    dispatch(
      startListLastCreativeTemplateProduces({
        templateID,
        excludeProduceID: produceID,
      })
    ).then((records) => {
      if (!cancelled) setLastProduceRecords(records || []);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, templateID, produceID]);

  const lastConfig = useMemo(() => {
    if (isLoadingDoc || columns.length === 0) return null;
    for (const record of lastProduceRecords) {
      const mappedRows = mapStoredRowsToColumns({
        storedRows: record.rows,
        storedColumns: record.columns,
        columns,
      });
      if (mappedRows.length > 0) return { record, rows: mappedRows };
    }
    return null;
  }, [columns, isLoadingDoc, lastProduceRecords]);

  const canLoadLastConfig =
    Boolean(lastConfig) &&
    !isProducing &&
    !hasNonEmptyRows({ rows: rows || [], columns });

  const previousColumnsRef = useRef({ templateID: "", columns: [] });
  useEffect(() => {
    if (!templateID || isLoadingDoc) return;
    const previous = previousColumnsRef.current;
    previousColumnsRef.current = { templateID, columns };
    if (previous.templateID !== templateID || isProducing) return;
    const renames = getRenamedColumnKeys({
      previousColumns: previous.columns,
      nextColumns: columns,
    });
    if (Object.keys(renames).length === 0) return;
    const migratedRows = (getValues("rows") || []).map((row) => {
      const nextRow = { ...row };
      Object.entries(renames).forEach(([oldKey, newKey]) => {
        if (!Object.prototype.hasOwnProperty.call(row, oldKey)) return;
        nextRow[newKey] = row[oldKey];
        delete nextRow[oldKey];
      });
      return nextRow;
    });
    replace(migratedRows);
  }, [columns, getValues, isLoadingDoc, isProducing, replace, templateID]);

  useEffect(() => {
    if (!templateID || isLoadingDoc || isProducing || columns.length === 0) {
      return;
    }
    const currentRows = getValues("rows") || [];
    if (
      produceID &&
      hydratedProduceIDRef.current !== produceID &&
      currentRows.length === 0
    ) {
      return;
    }
    const emptyRow = createEmptyRow({ columns });
    if (currentRows.length === 0) {
      append(emptyRow);
      return;
    }
    let needsReplace = false;
    const nextRows = currentRows.map((row) => {
      const hasAllKeys = columns.every((column) =>
        Object.prototype.hasOwnProperty.call(row, column.key)
      );
      if (hasAllKeys) return row;
      needsReplace = true;
      return { ...emptyRow, ...row };
    });
    if (needsReplace) {
      replace(nextRows);
    }
  }, [
    append,
    columns,
    getValues,
    isLoadingDoc,
    isProducing,
    produceColumnKeys,
    produceID,
    replace,
    templateID,
  ]);

  // Undo / redo over the rows table (Ctrl+Z / Ctrl+Y). Snapshots are debounced
  // so typing in a cell counts as a single step.
  const rowsHistoryRef = useRef({ stack: [], index: -1 });
  const rowsKey = useMemo(() => JSON.stringify(rows || []), [rows]);

  const commitRowsSnapshot = useCallback(() => {
    const history = rowsHistoryRef.current;
    const snapshot = JSON.stringify(getValues("rows") || []);
    if (snapshot === history.stack[history.index]) return;
    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(snapshot);
    if (history.stack.length > PRODUCE_HISTORY_LIMIT) history.stack.shift();
    history.index = history.stack.length - 1;
  }, [getValues]);

  useEffect(() => {
    rowsHistoryRef.current = { stack: [], index: -1 };
  }, [templateID, produceID]);

  useEffect(() => {
    const timeoutId = setTimeout(
      commitRowsSnapshot,
      PRODUCE_HISTORY_DEBOUNCE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [commitRowsSnapshot, rowsKey]);

  useEffect(() => {
    if (!canEditRows) return undefined;

    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const target = event.target;
      const isTyping =
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
      if (isTyping) return;

      const key = String(event.key || "").toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo = key === "y" || (key === "z" && event.shiftKey);
      if (!isUndo && !isRedo) return;

      commitRowsSnapshot();
      const history = rowsHistoryRef.current;
      const nextIndex = history.index + (isUndo ? -1 : 1);
      if (nextIndex < 0 || nextIndex >= history.stack.length) return;

      event.preventDefault();
      history.index = nextIndex;
      replace(JSON.parse(history.stack[nextIndex]));
      clearInvalidProduceMarks();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditRows, commitRowsSnapshot, replace]);

  const resetImageChecks = () => {
    rehostGenerationRef.current += 1;
    rehostGenerationByUrlRef.current = {};
    setCheckingImageUrls(new Set());
    setFailedImageUrls(new Set());
  };

  const resetRows = () => {
    replace([]);
    clearInvalidProduceMarks();
    resetImageChecks();
    rowRefs.current = {};
  };

  const applyHostedImageUrls = ({ urlMap = {} }) => {
    const currentRows = getValues("rows") || [];
    currentRows.forEach((row, index) => {
      columns.forEach((column) => {
        if (column.type !== "image") return;
        const current = String(row?.[column.key] || "").trim();
        if (!urlMap[current]) return;
        setValue(`rows.${index}.${column.key}`, urlMap[current], {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    });
  };

  const startRehostForRows = async ({
    rows: rowsToCheck = [],
    replaceExisting = false,
  } = {}) => {
    const urls = collectProduceImageUrls({
      rows: rowsToCheck,
      columns,
    });
    if (replaceExisting) {
      rehostGenerationByUrlRef.current = {};
    }
    if (urls.length === 0) {
      if (replaceExisting) {
        setCheckingImageUrls(new Set());
        setFailedImageUrls(new Set());
      }
      return;
    }
    const generation = rehostGenerationRef.current + 1;
    rehostGenerationRef.current = generation;
    urls.forEach((url) => {
      rehostGenerationByUrlRef.current[url] = generation;
    });
    setCheckingImageUrls((prev) => {
      const next = replaceExisting ? new Set() : new Set(prev);
      urls.forEach((url) => next.add(url));
      return next;
    });
    setFailedImageUrls((prev) => {
      const next = replaceExisting ? new Set() : new Set(prev);
      urls.forEach((url) => next.delete(url));
      return next;
    });

    const result = await dispatch(startRehostProduceRowImages({ urls }));
    const resultByUrl = new Map(
      (result?.results || []).map((item) => [item.sourceUrl, item])
    );
    const finishedUrls = urls.filter(
      (url) => rehostGenerationByUrlRef.current[url] === generation
    );
    const urlMap = {};
    const nextFailed = [];
    finishedUrls.forEach((url) => {
      const item = resultByUrl.get(url);
      if (item?.ok && item.url) {
        urlMap[url] = item.url;
        return;
      }
      nextFailed.push(url);
    });
    if (Object.keys(urlMap).length > 0) {
      applyHostedImageUrls({ urlMap });
    }
    setCheckingImageUrls((prev) => {
      const next = new Set(prev);
      finishedUrls.forEach((url) => next.delete(url));
      return next;
    });
    setFailedImageUrls((prev) => {
      const next = new Set(prev);
      finishedUrls.forEach((url) => next.delete(url));
      nextFailed.forEach((url) => next.add(url));
      return next;
    });
  };

  const onValidateTemplateChange = async (nextTemplateID) => {
    if (nextTemplateID === templateID) return true;
    const currentRows = getValues("rows") || [];
    if (!hasNonEmptyRows({ rows: currentRows, columns })) {
      resetRows();
      return true;
    }
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_PRODUCE_CHANGE_TEMPLATE_TITLE),
      text: t(TEMPLATES_PRODUCE_CHANGE_TEMPLATE_DESCRIPTION),
    });
    if (!confirmed.isConfirmed) return false;
    resetRows();
    return true;
  };

  const onLoadLastConfig = () => {
    if (!lastConfig || !canEditRows) return;
    const { record, rows: nextRows } = lastConfig;
    replace(nextRows);
    clearInvalidProduceMarks();
    if (record.language) setValue("language", record.language);
    if (record.variationCount != null) {
      setValue(
        "variationCount",
        clampProduceVariationCount({ value: record.variationCount })
      );
    }
    storedArtboardIDsRef.current = normalizeStoredIDs({
      value: record.artboardIDs,
    });
    setArtboardHydrateNonce((nonce) => nonce + 1);
    startRehostForRows({ rows: nextRows, replaceExisting: true });
  };

  const onTemplateChange = () => {
    lockHydratedArtboardsRef.current = false;
    storedArtboardIDsRef.current = [];
    setSelectedArtboardIDs([]);
    setIsPickingTemplate(false);
  };

  const onToggleArtboard = (artboardID) => {
    setSelectedArtboardIDs((prev) =>
      toggleSelectedID({
        availableIDs: availableArtboardIDs,
        selectedIDs: prev,
        id: artboardID,
      })
    );
  };

  const onAddRow = () => {
    if (!canEditRows) return;
    append(createEmptyRow({ columns }));
    clearInvalidProduceMarks();
  };

  const onRemoveRow = ({ index }) => {
    remove(index);
    clearInvalidProduceMarks();
  };

  const onChangeImage = ({ index, key, nextUrl }) => {
    setValue(`rows.${index}.${key}`, nextUrl, {
      shouldDirty: true,
      shouldTouch: true,
    });
    clearInvalidProduceCell({ index, key });
  };

  const onCopyValueToColumn = ({ index, key }) => {
    const currentRows = getValues("rows") || [];
    const value = currentRows[index]?.[key];
    if (!value) return;
    replace(currentRows.map((row) => ({ ...row, [key]: value })));
    currentRows.forEach((_row, rowIndex) => {
      if (rowIndex === index) return;
      clearInvalidProduceCell({ index: rowIndex, key });
    });
  };

  const applyCsvRows = ({ parsedRows, mode }) => {
    if (mode === "replace") {
      replace(parsedRows);
    } else {
      replace([...(getValues("rows") || []), ...parsedRows]);
    }
    clearInvalidProduceMarks();
    startRehostForRows({
      rows: parsedRows,
      replaceExisting: mode === "replace",
    });
  };

  const onUploadCsv = async ({ file }) => {
    if (!canEditRows) return;
    let parsedRows = [];
    try {
      parsedRows = await parseProduceCsvFile({ file, columns });
    } catch {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_CSV_ERROR),
        icon: "error",
      });
      return;
    }
    if (parsedRows.length === 0) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_CSV_EMPTY),
        icon: "warning",
      });
      return;
    }
    const currentRows = getValues("rows") || [];
    if (currentRows.length === 0) {
      applyCsvRows({ parsedRows, mode: "replace" });
      return;
    }
    const result = await ConfirmAlert({
      title: t(TEMPLATES_PRODUCE_CSV_TITLE),
      text: t(TEMPLATES_PRODUCE_CSV_DESCRIPTION),
      confirmButtonText: t(TEMPLATES_PRODUCE_CSV_REPLACE),
      thirdButtonText: t(TEMPLATES_PRODUCE_CSV_APPEND),
      cancelButtonText: t(BUTTON_GO_BACK),
      showThirdButton: true,
    });
    if (result.isConfirmed) {
      applyCsvRows({ parsedRows, mode: "replace" });
      return;
    }
    if (result.isDenied) {
      applyCsvRows({ parsedRows, mode: "append" });
    }
  };

  const onDownloadConfig = () => {
    const currentRows = getValues("rows") || [];
    if (currentRows.length === 0) return;
    const name = selectedCard?.name || doc?.name;
    downloadProduceRowsCsv({
      columns,
      rows: currentRows,
      templateName: name,
      fileName: t(TEMPLATES_PRODUCE_CONFIG_FILENAME, {
        name: name || "template",
      }),
    });
  };

  const onDeleteGeneratedItems = async ({ items: itemsToDelete = [] } = {}) => {
    const creativeIDs = itemsToDelete.flatMap((item) =>
      item.isCarousel && Array.isArray(item.carouselImages)
        ? item.carouselImages.map((slide) => slide.id)
        : [item.id]
    );
    return dispatch(startDeleteProduceCreatives({ produceID, creativeIDs }));
  };

  const onDownloadFormat = () => {
    if (!canEditRows) return;
    const templateName = selectedCard?.name || doc?.name;
    downloadProduceFormatCsv({
      columns,
      templateName,
      fileName: t(TEMPLATES_PRODUCE_CSV_EXAMPLE_FILENAME, {
        name: templateName || "template",
      }),
      exampleText: ({ label }) =>
        t(TEMPLATES_PRODUCE_CSV_EXAMPLE_TEXT, { label }),
    });
  };

  const collectProduceJobInput = async () => {
    if (isProducing || hasActiveProduceRun({ produceRecord })) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_ACTIVE_RUN),
        icon: "warning",
      });
      return null;
    }
    if (!templateID) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_SELECT_TEMPLATE),
        icon: "warning",
      });
      return null;
    }
    if (isLoadingDoc || isCheckingImages) return null;
    if (hasUnusableImages) {
      if (imageInvalidRowIndexes[0] != null) {
        scrollToProduceRow({ rowRefs, index: imageInvalidRowIndexes[0] });
      }
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_IMAGES_UNUSABLE),
        icon: "error",
      });
      return null;
    }
    if (!isValidProduceVariationCount({ value: getValues("variationCount") })) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_VALIDATION_VARIATIONS),
        icon: "warning",
      });
      return null;
    }
    let persistRows = [];
    if (hasVariables) {
      persistRows = getValues("rows") || [];
      if (persistRows.length === 0) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_VALIDATION_NO_ROWS),
          icon: "warning",
        });
        return null;
      }
    } else {
      persistRows = createVariationRows({
        count: getValues("variationCount"),
      });
    }
    const artboardIDsToProduce =
      selectedArtboardIDs.length > 0
        ? selectedArtboardIDs
        : availableArtboardIDs;
    if (artboardIDsToProduce.length === 0) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS),
        icon: "warning",
      });
      return null;
    }

    const requestedRowCount = getProduceProductionRowCount({
      rowCount: persistRows.length,
      variationCount: getValues("variationCount"),
      hasVariables,
    });
    const produceGate = await blockIfProduceBatchExceeded({
      requestedCount: requestedRowCount,
    });
    if (produceGate.blocked) {
      if (!produceGate.paywallOpened) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_VALIDATION_TOO_MANY_ROWS, {
            max: produceGate.limit,
            count: requestedRowCount,
          }),
          icon: "warning",
        });
      }
      return null;
    }
    const nextInvalid = applyInvalidProduceMarks({
      nextRows: persistRows,
      nextColumns: columns,
    });
    if (nextInvalid.length > 0) {
      scrollToProduceRow({ rowRefs, index: nextInvalid[0] });
      SimpleAlert({
        title: t(
          hasInvalidHighlightPhrase({ rows: persistRows, columns })
            ? TEMPLATES_PRODUCE_VALIDATION_HIGHLIGHT
            : TEMPLATES_PRODUCE_VALIDATION_ERROR
        ),
        icon: "error",
      });
      return null;
    }

    return {
      persistRows,
      jobRows: persistRows,
      artboardIDsToProduce,
      generateCount: requestedRowCount * produceCellCount,
      requestedRowCount,
    };
  };

  const startProduceWithInput = async ({ input }) => {
    if (!input) return;
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: input.generateCount,
      })
    );
    if (isBlocked) return;

    const nextProduceID = produceID || dispatch(getKeyDatabase());
    if (!produceID) {
      setProduceID(nextProduceID);
    }
    hydratedProduceIDRef.current = nextProduceID;
    pendingScrollToResultsRef.current = true;
    setPendingTargetCount(galleryItems.length + input.generateCount);
    setIsStartingProduce(true);
    try {
      const result = await dispatch(
        startProduceCreativeTemplate({
          templateID,
          produceID: nextProduceID,
          language,
          rows: input.persistRows,
          jobRows: input.jobRows,
          jobVariationCount: input.jobVariationCount,
          columns,
          artboardIDs: input.artboardIDsToProduce,
          cellCount: produceCellCount,
          variationCount: getValues("variationCount"),
        })
      );
      if (!result?.ok && result?.reason !== "produceFailed") {
        setPendingTargetCount(0);
      }
      if (result?.reason === "tooManyRows") {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_VALIDATION_TOO_MANY_ROWS, {
            max: PRODUCE_MAX_ROWS,
            count: input.requestedRowCount,
          }),
          icon: "warning",
        });
      }
    } finally {
      setIsStartingProduce(false);
    }
  };

  const onGenerate = async () => {
    const input = await collectProduceJobInput();
    await startProduceWithInput({ input });
  };

  const onEditItem = ({ item, carouselIndex, url } = {}) => {
    const slideItem = resolveProduceGallerySlideItem({
      item,
      carouselIndex,
      url,
    });
    if (!slideItem?.url) return;
    setEditItem(slideItem);
  };

  const onRetryFailed = useCallback(async () => {
    if (
      isProducing ||
      isRetryingProduce ||
      hasActiveProduceRun({ produceRecord })
    ) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_ACTIVE_RUN),
        icon: "warning",
      });
      return;
    }
    const failedCount =
      failedGalleryItems.length || Number(produceSummary.failed) || 0;
    const retryFromJobID = latestProduceRun?.jobID;
    if (!templateID || !produceID || !retryFromJobID || failedCount <= 0) {
      return;
    }
    const artboardIDsToProduce =
      selectedArtboardIDs.length > 0
        ? selectedArtboardIDs
        : availableArtboardIDs;
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: failedCount,
      })
    );
    if (isBlocked) return;
    setPendingTargetCount(
      Math.max(Number(produceRecord?.targetCreativeCount) || 0, 0) + failedCount
    );
    setIsRetryingProduce(true);
    try {
      await dispatch(
        startRetryFailedProduceCreativeTemplate({
          templateID,
          produceID,
          retryFromJobID,
          failedCount,
          language,
          artboardIDs: artboardIDsToProduce,
          artboardCount: Math.max(artboardIDsToProduce.length, 1),
        })
      );
    } finally {
      setIsRetryingProduce(false);
    }
  }, [
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    availableArtboardIDs,
    dispatch,
    failedGalleryItems.length,
    isProducing,
    isRetryingProduce,
    language,
    latestProduceRun?.jobID,
    produceID,
    produceRecord,
    produceSummary.failed,
    selectedArtboardIDs,
    t,
    templateID,
  ]);

  const resultsGallery = (
    <ProduceResultsGallery
      items={galleryItems}
      failedItems={failedGalleryItems}
      summary={produceSummary}
      isGenerating={isProducing}
      pendingCount={pendingCount}
      onEditItem={onEditItem}
      onRetryFailed={onRetryFailed}
      isRetrying={isRetryingProduce}
      runs={produceRecord?.runs}
      onDeleteItems={canWrite ? onDeleteGeneratedItems : null}
      columns={
        hasVariables
          ? { xs: 1, sm: 2, md: 3, lg: 4 }
          : { xs: 1, sm: 2, md: 2, lg: 3 }
      }
    />
  );

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        flex: 1,
        px: 0,
        pt: 0,
        pb: 0,
        alignItems: "stretch",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          width: "100%",
          pt: 3,
          pb: 2,
          px: 3,
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "background.paper",
        }}
      >
        <Box>
          <TitlePage>{t(TEMPLATES_PRODUCE_TITLE)}</TitlePage>
          <Typography color="text.secondary">
            {t(TEMPLATES_PRODUCE_DESCRIPTION)}
          </Typography>
        </Box>
        <IconButton
          className="close-modal-button"
          aria-label={t(BUTTON_CLOSE)}
          onClick={onBackToList}
          sx={{
            p: 0.5,
            fontSize: "1.4rem",
            color: "background.paperSecondaryContrastText",
            backgroundColor: "background.paperSecondary",
            "&:hover": {
              color: "background.paperSecondaryContrastText",
              backgroundColor: "background.paperSecondary",
              filter: theme.palette.hover.main,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ width: "100%", px: 3, pt: 2.5, pb: 3 }}>
        {isLoadingCards ? (
          <Loader hasMessage message={t(LOADING_INFORMATION)} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr)" },
              gap: 3,
              width: "100%",
              alignItems: "start",
              pb: 5,
            }}
          >
            <ProduceTemplateCard
              control={control}
              selectedCard={selectedCard}
              hasProduceableCards={produceableCards.length > 0}
              showTemplateSelect={showTemplateSelect}
              templateOptions={templateOptions}
              templateName={templateName}
              showFormatToggles={showFormatToggles}
              formatOptions={formatOptions}
              selectedArtboardIDs={selectedArtboardIDs}
              showVariationCount={showVariationCount}
              hasVariables={hasVariables}
              setValue={setValue}
              rowCount={rowCount}
              variationCount={variationRowCount}
              selectedArtboardCount={selectedArtboardCount}
              slideCount={produceSlideCount}
              totalCount={totalCount}
              isProducing={isProducing}
              onValidateTemplateChange={onValidateTemplateChange}
              onTemplateChange={onTemplateChange}
              onToggleArtboard={onToggleArtboard}
              onPickTemplate={() => setIsPickingTemplate(true)}
              onEditTemplate={() => {
                if (!templateID) return;
                openTemplateEditor({
                  templateID,
                  returnToProduce: true,
                });
              }}
              canEditTemplate={canWrite}
              onGenerate={onGenerate}
              isCheckingImages={isCheckingImages}
              hasUnusableImages={hasUnusableImages}
            />
            <Box
              sx={{
                minWidth: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!templateID ? (
                <Typography color="text.secondary">
                  {t(TEMPLATES_PRODUCE_SELECT_TEMPLATE)}
                </Typography>
              ) : isLoadingDoc ? (
                <Loader hasMessage message={t(LOADING_INFORMATION)} />
              ) : hasVariables ? (
                <ProduceRowsTable
                  key={templateID}
                  control={control}
                  columns={columns}
                  fields={fields}
                  rows={rows}
                  invalidRowIndexes={tableInvalidRowIndexes}
                  invalidCellKeysByRow={invalidCellKeysByRow}
                  onClearInvalidCell={clearInvalidProduceCell}
                  disabled={!canEditRows}
                  onAddRow={onAddRow}
                  onRemoveRow={onRemoveRow}
                  onChangeImage={onChangeImage}
                  onCopyValueToColumn={onCopyValueToColumn}
                  onUploadCsv={onUploadCsv}
                  onLoadLastConfig={
                    canLoadLastConfig && canEditRows ? onLoadLastConfig : null
                  }
                  onDownloadFormat={onDownloadFormat}
                  onDownloadConfig={onDownloadConfig}
                  rowRefs={rowRefs}
                  checkingImageUrls={checkingImageUrls}
                  failedImageUrls={failedImageUrls}
                />
              ) : showGallery ? (
                <Box ref={resultsRef} sx={{ scrollMarginTop: 96, minWidth: 0 }}>
                  {resultsGallery}
                </Box>
              ) : null}
            </Box>
            {showGallery && hasVariables ? (
              <Box
                ref={resultsRef}
                sx={{ gridColumn: "1 / -1", scrollMarginTop: 96 }}
              >
                {resultsGallery}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
      <ModalCreativeTemplateEdit
        isOpen={Boolean(editItem)}
        onClose={() => setEditItem(null)}
        produceID={produceID}
        item={editItem}
      />
      <FeatureAddonPaywallDialog
        open={templatesPaywallOpen}
        onClose={closeTemplatesPaywall}
        status={templatesPaywallStatus}
        addonKey={FEATURE_ADDON_KEYS.TEMPLATES}
        isLoading={templatesPaywallLoading}
        isCheckingOut={templatesCheckingOut}
        onCheckoutTier={checkoutTemplatesTier}
      />
    </ContainerPage>
  );
};

export default TemplateProduce;
